import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { box, text, grid, boxCustom, line, textWrap, labelValue, addAnuladoWatermark } from "../pdfDraw";
import { formatDate, formatDecimal, formatText } from "../../formatters";
import { getRetencionISLR } from "../../../controllers/apiWithholdings";
import { getClientLogo } from "../../../controllers/api_clients";
import exampleLogo from "../../../assets/img/react.jpg";
import QRCode from "qrcode";
import { encryptText } from '../../../controllers/api';

/* ================================================= */
export const buildISLRPDF = async (comprobante_id, withholdingId, mode = "download") => {

  const data = await getRetencionISLR(withholdingId);
  if (!data) return;

  if (data.empresa.logo_url){
    var empresa_id = data.empresa.logo_url.split('/')[3]
    data.empresa.logo_base64 = await getClientLogo(empresa_id);
  }

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "letter"
  });

  const g = grid();
  const col12 = g.col(0, 12);

  let y = 10;

  const {
    cabecera,
    empresa,
    proveedor_beneficiario,
    detalle,
    totales,
    firmas,
    tercero  // (2026-05-18 ADJ-1) ventas por cuenta de terceros (puede ser null)
  } = data;

  /* ================================================= */
  /* ================= HEADER ======================== */
  /* ================================================= */

  const headerHeight = 30;

  const colLogo = g.col(0, 2);
  const colCenter = g.col(2, 7);
  const colRight = g.col(9, 3);

  // BORDE GENERAL (único)
  box(doc, col12.x, y, col12.w, headerHeight);

  /* ---------- LOGO ---------- */

  const logoY = y + 3;
  const logoH = headerHeight - 6;

  const logoImage = empresa?.logo_base64 || exampleLogo;

  try {
    doc.addImage(
      logoImage,
      empresa?.logo_base64 ? "PNG" : "JPG",
      colLogo.x + 2,
      logoY,
      colLogo.w - 4,
      logoH
    );
  } catch (err) {
    console.warn("Error cargando logo, usando fallback");

    doc.addImage(
      exampleLogo,
      "JPG",
      colLogo.x + 2,
      logoY,
      colLogo.w - 4,
      logoH
    );
  }

  /* ---------- QR ---------- */

  const baseUrl = window.location.origin;

  const qrUrl = `${baseUrl}/document/RETISLR/${encodeURIComponent(encryptText(cabecera.qr_token.toString()))}/`;

  const qrBase64 = await QRCode.toDataURL(qrUrl, {
    width: 120,
    margin: 1
  });
  const qrSize = 25;
  /*
   * @param {object} doc - Instancia de jsPDF
   * @param {number} x - Posición X inicial
   * @param {number} y - Posición Y inicial
   * @param {number} w - Ancho de la caja
   * @param {number} h - Alto de la caja
   * @param {string|array} fillColor - Color de fondo (hex o RGB) (default "#FFFFFF")
   * @param {number} borderRadius - Radio de los bordes (default 0)
   * @param {number} borderWidth - Grosor del borde en mm (default 0, sin borde)
   * @param {string|array} borderColor - Color del borde (hex o RGB) (default "#000000")
  */
  boxCustom(doc, (colRight.x + colRight.w - 29), (y + 1.5), qrSize + 2, qrSize + 2, "#ffffff", 2, 0.5, "#4a4a4a");
  doc.addImage(
    qrBase64,
    "PNG",
    colRight.x + colRight.w - 28,
    y + 2.5,
    qrSize,
    qrSize
  );

  /* ---------- CENTRO ---------- */

  text(
    doc,
    cabecera.titulo,
    colCenter.x + colCenter.w / 2,
    y + 10,
    11,
    "center",
    true
  );

  text(
    doc,
    `Fecha: Desde ${formatDate(cabecera.periodo_desde)} Hasta ${formatDate(cabecera.periodo_hasta)}`,
    colCenter.x + colCenter.w / 2,
    y + 18,
    9,
    "center"
  );

  /* ---------- DERECHA (SIN CAJAS) ---------- */

  const rightX = colRight.x + 2;

  labelValue(
    doc,
    "Nº Comprobante",
    cabecera.numero_comprobante,
    rightX - 15,
    y + 7
  );

  labelValue(
    doc,
    "Fecha Emisión",
    formatDate(cabecera.fecha_emision),
    rightX - 15,
    y + 12
  );

  // (2026-07-18 A3/A4) Nº de Control PROPIO del comprobante, destacado en rojo/
  // bold (estilo consistente con facturas). Se OMITE si el backend no lo entrega
  // (comprobantes históricos sin control del lote).
  if (cabecera.numero_control) {
    doc.setTextColor(220, 38, 38); // tailwind red-600
    text(doc, `Nº de Control: ${cabecera.numero_control}`, rightX - 15, y + 17, 9, "left", true);
    doc.setTextColor(0, 0, 0); // reset a negro
  }

  y += headerHeight + 4;

  /* ================================================= */
  /* ========= PROVEEDOR / EMPRESA =================== */
  /* ================================================= */

  let hInfo = 24;
  const colLeft = g.col(0, 6);
  const colRightInfo = g.col(6, 6);
  let yLn = 1;
  var yPro = textWrap(
    doc,
    "                    " + formatText(proveedor_beneficiario.direccion2) || "",
    0, // X no importa para medir
    0,
    colLeft.x + 2,
    7,
    "justify",
    false,
    5,
    true // measureOnly
  );
  var yEmp = textWrap(
    doc,
    "                    " + formatText(empresa.direccion2) || "",
    0, // X no importa para medir
    0,
    colRightInfo.x + 2,
    7,
    "justify",
    false,
    5,
    true // measureOnly
  );
  if (yPro > yEmp){
    yLn = (yPro / 2);
    hInfo = hInfo - 5 + yPro;
  }
  if (yEmp > yPro){
    yLn = (yEmp / 2);
    hInfo = hInfo - 5 + yEmp;
  }
  //console.log('yPro: ', yPro);
  //console.log('yEmp: ', yEmp);

  // (2026-05-18) Posiciones INVERTIDAS y renombre: el conjunto que antes
  // se rotulaba "Proveedor/Beneficiario" en realidad es el CLIENTE FINAL.
  // Izquierda = Empresa (agente); Derecha = Cliente Final.

  // Empresa (izquierda)
  box(doc, colLeft.x, y, colLeft.w, hInfo);

  text(doc, "EMISOR:", colLeft.x + 2, y + 5, 8, "left", true);
  text(doc, formatText(empresa.nombre), colLeft.x + 2, y + 10);
  labelValue(
    doc,
    "RIF",
    formatText(empresa.rif),
    colLeft.x + 2,
    y + 15
  );
  labelValue(
    doc,
    "Dirección",
    "",
    colLeft.x + 2,
    y + 20
  );

  textWrap(
    doc,
    "                         " + formatText(empresa.direccion) || "",
    colLeft.x + 2, // X no importa para medir
    y + 20,
    colLeft.w - 3,
    7,
    "justify",
    false,
    5
  );

  // Cliente Final (derecha) — datos en proveedor_beneficiario
  box(doc, colRightInfo.x, y, colRightInfo.w, hInfo);

  text(doc, "CLIENTE (Sujeto Retenido):", colRightInfo.x + 2, y + 5, 8, "left", true);
  text(doc, formatText(proveedor_beneficiario.nombre), colRightInfo.x + 2, y + 10);
  labelValue(
    doc,
    "RIF",
    formatText(proveedor_beneficiario.rif),
    colRightInfo.x + 2,
    y + 15
  );
  labelValue(
    doc,
    "Dirección",
    "",
    colRightInfo.x + 2,
    y + 20
  );

  textWrap(
    doc,
    "                         " + formatText(proveedor_beneficiario.direccion) || "",
    colRightInfo.x + 2, // X no importa para medir
    y + 20,
    colRightInfo.w - 3,
    7,
    "justify",
    false,
    5
  );

  // (2026-05-18) Antes y += hInfo + (yLn*5): el yLn*5 dejaba un hueco
  // vacío grande antes del bloque de terceros. Pequeño gap fijo.
  y += hInfo + 4;

  /* ================================================= */
  /* ====== VENTAS POR CUENTA DE TERCEROS (si aplica) = */
  /* (2026-05-18 ADJ-1) Mismo bloque que la factura,    */
  /* adaptado al formato en caja del comprobante ISLR.  */
  /* ================================================= */
  if (tercero) {
    const colT = g.col(0, 12);
    const hT = 21;
    box(doc, colT.x, y, colT.w, hT);
    text(doc, "Ventas por cuenta de terceros", colT.x + 2, y + 5, 8, "left", true);
    const colT2 = colT.x + colT.w / 2;
    // (2026-05-18) labelValue ya añade ":" -> las etiquetas NO deben
    // llevarlo (evita el "::" duplicado).
    labelValue(doc, "Nombre / Razón Social", formatText(tercero.nombre_empresa || tercero.denominacion_comercial || ""), colT.x + 2, y + 10, 7, "left");
    labelValue(doc, "R.I.F.", formatText(tercero.rif || ""), colT2, y + 10, 7, "left");
    labelValue(doc, "Dirección", formatText(tercero.direccion_fiscal || ""), colT.x + 2, y + 15, 7, "left");
    labelValue(doc, "Teléfono", formatText(tercero.telefono || ""), colT.x + 2, y + 19, 7, "left");
    labelValue(doc, "Correo", formatText(tercero.email || ""), colT2, y + 19, 7, "left");
    y += hT + 4;
  }

  /* ================================================= */
  /* ================= TABLA ========================= */
  /* ================================================= */

  const tableData = detalle.map(d => [
    formatDate(d.fecha),
    d.numero_pago,
    d.tipo,
    d.nro_documento,
    formatDecimal(d.monto_documento),
    d.codigo_concepto,
    formatDecimal(d.monto_abonado),
    formatDecimal(d.monto_objeto_retencion),
    d.tarifa_porcentaje,
    formatDecimal(d.sustraendo),
    formatDecimal(d.impuesto_retenido)
  ]);

  autoTable(doc, {
    startY: y,
    head: [[
      "Fecha",
      "Número Pago",
      "Tipo",
      "N° Doc.",
      "Monto Documento",
      "Código Concepto",
      "Monto Abonado",
      "Monto Objeto Retención",
      "Tarifa %",
      "Sustraendo",
      "Impuesto Retenido"
    ]],
    body: tableData,
    foot: [[
      "", "", "", "", "", "", "",
      formatDecimal(totales.monto_objeto_retencion),
      "",
      "",
      formatDecimal(totales.impuesto_retenido)
    ]],
    theme: "grid",
    styles: { fontSize: 8 },
    headStyles: { fillColor: [220,220,220], textColor:0, fontStyle: "bold", halign: "center" },
    footStyles: { fillColor: [220,220,220], textColor:0, fontStyle: "bold", halign: "right" },
    columnStyles: { 
        0: {halign:'center'}, 
        1: {halign:'center'}, 
        2: {halign:'center'}, 
        3:{halign:'center'}, 
        4:{halign:'right'}, 
        5:{halign:'center'}, 
        6:{halign:'right'}, 
        7:{halign:'right'}, 
        8:{halign:'center'}, 
        9:{halign:'right'},
        10:{halign:'right'} 
    },
    margin: { left: col12.x, right: 10 },
    tableWidth: col12.w
  });

  // (2026-07-18 A3/A4) Pie de imprenta (leyenda_legal del lote), debajo de la
  // tabla. Se imprime verbatim (normal) SOLO si el backend lo entrega. El ISLR
  // no destructura `legal`, se lee de data.legal directamente.
  const _islrLegal = (data.legal && data.legal.leyenda_legal) || "";
  if (_islrLegal) {
    const _legalY = (doc.lastAutoTable ? doc.lastAutoTable.finalY : y) + 6;
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    doc.text(_islrLegal, col12.x + 2, _legalY, { maxWidth: col12.w, align: "left" });
  }

  /* ================================================= */
  /* ================= FOOTER ======================== */
  /* ================================================= */

  const footerHeight = 30;
  const footerTop = doc.internal.pageSize.height - footerHeight;

  text(
    doc,
    "Firma del Agente de Retención: __________________________",
    col12.x + 5,
    footerTop + 10,
    9
  );

  text(
    doc,
    `Fecha de Entrega: ${formatDate(firmas.fecha_entrega)}`,
    col12.x + 5,
    footerTop + 18,
    9
  );
  /* ================= GUARDAR PDF ================= */
  // (2026-07-08) Sello "ANULADO" en todas las páginas si el comprobante está anulado.
  if (cabecera.estatus === "anulado" || cabecera.estatus === "anulada") {
    const _pc = doc.getNumberOfPages();
    for (let _p = 1; _p <= _pc; _p++) { doc.setPage(_p); addAnuladoWatermark(doc, cabecera.fecha_anulacion); }
  }
  const pdfBlob = doc.output("blob");
  if(mode === "download"){
    doc.save(`RET_ISLR_${cabecera.numero_comprobante}.pdf`);
  } else {
    return URL.createObjectURL(pdfBlob);
  }
};